import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { BrandLogo } from '@/components/branding/BrandLogo';
import { getBuildVersion } from '@/lib/build-version';
import { useNotificationsUnread } from '@/features/notifications/api/use-notifications-unread';
import { NavGroup, findActiveGroupId } from './NavGroup';
import { NAV_GROUPS } from './nav-config';

/**
 * Application sidebar — canonical SustEvo App Shell skeleton.
 *
 * Per `sustevo-handbook/brand/APP_SHELL.md` (and matched line-for-line
 * against the PM canonical at
 * `Sustevo-Core/project-management-tool/src/components/AppShell.tsx`):
 *
 *   - Single `<aside>` landmark. The slide-in transform + sticky
 *     classes live HERE, not in a wrapping element — preserves a clean
 *     a11y tree (one "complementary" landmark, not two).
 *   - Three-band column: brand band (§ 3) / nav block (§ 6) / footer (§ 7).
 *   - 15rem wide, sticky on `md+`, fixed slide-in panel on `<md` driven
 *     by `open` / `onClose` props lifted to `<AppShell>`.
 *   - Nav rows match § 6.2 (2×16px active vertical bar via `border-l-2`
 *     per HARMONIZATION.md § 13, Phosphor at 20px chrome size,
 *     `px-2 py-1.5 text-sm font-medium`). Groups render with eyebrow
 *     labels per § 6.3
 *     — never collapsible accordions. The first group is rendered flat
 *     under the nav (no eyebrow, no extra padding wrapper) per PM's
 *     flat-top + eyebrow-grouped pattern.
 *   - Footer (§ 7, simplified 2026-05-21): version + API host only.
 *     Profile, sign-out, language all moved to the top bar's UserMenu
 *     (§ 8) or to the Settings page.
 */
export interface SidebarProps {
  className?: string;
  /** Open state for the mobile slide-in panel. Driven by `<AppShell>`. */
  open?: boolean;
  /** Called when the panel should close (e.g. backdrop click, route change). */
  onClose?: () => void;
}

export function Sidebar({
  className,
  open = false,
  onClose: _onClose,
}: SidebarProps): JSX.Element {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  // Retained for telemetry / future highlighting — no longer drives
  // expand/collapse since groups are flat eyebrow-labelled lists now.
  useMemo(() => findActiveGroupId(NAV_GROUPS, pathname), [pathname]);
  const { version, apiHost } = getBuildVersion();
  const notificationsUnread = useNotificationsUnread();

  // Scroll-aware shadow on the brand band per
  // `sustevo-handbook/brand/APP_SHELL.md` § 3. Shadow is invisible
  // when the nav is at the top; fades in over 200ms the moment any
  // content has scrolled past the brand band edge.
  const navRef = useRef<HTMLElement | null>(null);
  const [navScrolled, setNavScrolled] = useState(false);
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const onScroll = (): void => {
      setNavScrolled(nav.scrollTop > 1);
    };
    nav.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => nav.removeEventListener('scroll', onScroll);
  }, []);
  const trailingCountByRoute = useMemo(
    () => ({ '/notifications': notificationsUnread }),
    [notificationsUnread],
  );

  // Canonical product label for the brand band. Defaults to "SENVIRO".
  const productLabel = t('app.brandLabel', { defaultValue: 'SENVIRO' });

  return (
    <aside
      id="app-shell-sidebar"
      data-testid="sidebar"
      data-mobile-drawer
      data-tour="sidebar-nav"
      data-open={open ? 'true' : 'false'}
      aria-label={t('mobile.drawer.aria.nav', { defaultValue: 'Navigation drawer' })}
      // `tabIndex={-1}` so the AppShell focus-on-open effect can move
      // focus to the landmark without inserting it into the natural Tab
      // order. Keyboard users still Tab from the brand band straight to
      // the first nav link.
      tabIndex={-1}
      className={cn(
        // Mobile: fixed 280px slide-in panel per HARMONIZATION.md § 8.2 row
        // "MobileDrawer (left/right edge)". Desktop: 15rem (240px) sticky
        // column per APP_SHELL.md § 2. `motion-reduce:transition-none`
        // honours `prefers-reduced-motion` per HARMONIZATION.md § 12 —
        // the panel jumps to its target position without animating.
        // BRAND.md § 3.6 token discipline — chrome reads the canonical ink
        // scale directly (light/dark pair) instead of the shadcn
        // `border-border` / `bg-card` aliases. Dark background matches the
        // brand band below (ink-900) so the panel is a single flat surface.
        'fixed inset-y-0 left-0 z-40 flex h-screen w-[280px] flex-col border-r border-[var(--color-ink-200)] bg-[var(--color-ink-0)] transition-transform duration-200 ease-out motion-reduce:transition-none dark:border-[var(--color-ink-700)] dark:bg-[var(--color-ink-900)]',
        open ? 'translate-x-0' : '-translate-x-full',
        'md:sticky md:top-0 md:w-[15rem] md:translate-x-0',
        className,
      )}
    >
      {/* Brand band — APP_SHELL.md § 3. Scroll-aware shadow fades in
          when the nav block below has scrolled past its top edge,
          giving a clear visual divider at the chrome ↔ scroll-area
          boundary (Linear / Notion / Vercel pattern).

          Shadow intensity calibrated 2026-05-21: bumped from
          `0 1px 3px -1px rgba(10,14,19,0.08)` (too subtle, user
          couldn't see the divider) to `0 4px 8px -2px
          rgba(10,14,19,0.12)` — softer falloff, more visible. Stops
          short of "drop shadow"; still reads as a chrome divider. */}
      <div
        data-testid="brand-band"
        data-scrolled={navScrolled ? 'true' : 'false'}
        // HARMONIZATION.md § 8.1: brand band is `h-16` (64px). Vertical
        // centring on the logo + product label via `items-center`. The
        // earlier `pb-4 pt-6` padding-based sizing produced ~60px,
        // ~4px shy of the canonical chrome measurement.
        className={cn(
          'relative z-10 flex h-16 items-center gap-2.5 bg-[var(--color-ink-0)] dark:bg-[var(--color-ink-900)] border-b border-[var(--color-ink-100)] dark:border-[var(--color-ink-700)] px-5 transition-shadow duration-200',
          navScrolled && 'shadow-[0_4px_8px_-2px_rgba(10,14,19,0.12)]',
        )}
      >
        <BrandLogo themed height={28} />
        <span
          aria-hidden="true"
          className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--color-ink-400)] dark:text-[var(--color-ink-300)]"
        >
          {productLabel}
        </span>
      </div>

      {/* Nav block — APP_SHELL.md § 6. */}
      <nav
        ref={navRef}
        className="scrollbar-elegant scroll-fade-y flex-1 overflow-y-auto px-3 py-2"
        aria-label={t('nav.groups.workspace')}
      >
        {NAV_GROUPS.map((group, index) => (
          <NavGroup
            key={group.id}
            group={group}
            suppressLabel={index === 0}
            trailingCountByRoute={trailingCountByRoute}
          />
        ))}
      </nav>

      {/* Footer — APP_SHELL.md § 7. Simplified 2026-05-21: version +
          API host only. Profile, sign-out, language all live in the
          top bar's UserMenu (§ 8) or in Settings page. */}
      <div className="border-t border-[var(--color-ink-200)] px-4 py-4 dark:border-[var(--color-ink-700)]">
        <p className="font-mono text-[11px] text-[var(--color-ink-600)] dark:text-[var(--color-ink-400)]">
          {version}
        </p>
        <p className="font-mono text-[11px] text-[var(--color-ink-600)] dark:text-[var(--color-ink-400)]">
          {apiHost}
        </p>
      </div>
    </aside>
  );
}
